import buttonBgDark from '@assets/images/button-bg-dark.svg';
import buttonBgLight from '@assets/images/button-bg-white.svg';

export const components = {
  Heading: {
    baseStyle: {
      color: `transparent`,
      backgroundClip: `text`,
      WebKitBackgroundClip: `text`,
      backgroundImage: `linear-gradient(90deg, #cea558 0%, #fbe09b 40%, #cea558 100%)`,
      mb: { base: 4, md: 6 }
    },
    sizes: {
      xs: {
        fontSize: [`xs`, `xs`, `sm`],
        letterSpacing: [`.0825rem`, `.0825rem`, `.09625rem`]
      },
      sm: {
        fontSize: [`sm`, `sm`, `md`],
        letterSpacing: [`.0825rem`, `.0825rem`, `.11rem`]
      },
      md: {
        fontSize: [`lg`, `lg`, `xl`],
        letterSpacing: [`.1237rem`, `.1237rem`, `.1375rem`]
      },
      lg: {
        fontSize: [`xl`, `xl`, `2xl`],
        letterSpacing: [`.1375rem`, `.1375rem`, `.165rem`]
      },
      xl: {
        fontSize: `3xl`,
        letterSpacing: [`.20625rem`]
      },
      '2xl': {
        fontSize: [`3xl`, `3xl`, `4xl`],
        letterSpacing: [`.20625rem`, `.20625rem`, `.2475rem`]
      },
      '3xl': {
        fontSize: [`4xl`, `4xl`, `5xl`],
        letterSpacing: [`.2475rem`, `.2475rem`, `.33rem`]
      },
      '4xl': {
        fontSize: [`4xl`, `4xl`, `5xl`, `6xl`],
        letterSpacing: [`.2475rem`, `.2475rem`, `.33rem`, `.4125rem`]
      },
      '5xl': {
        fontSize: [`4xl`, `4xl`, `6xl`, `8xl`],
        lineHeight: `none`,
        letterSpacing: [`.2475rem`, `.2475rem`, `.4125rem`, `.6875rem`]
      }
    },
    variants: {
      detail: {
        fontFamily: `body`,
        fontWeight: `600`
      }
    }
  },
  Text: {
    baseStyle: {
      mb: { base: 4, md: 6 }
    }
  },
  List: {
    baseStyle: {
      container: {
        mb: { base: 4, md: 6 }
      }
    }
  },
  Button: {
    defaultProps: {
      colorScheme: null
    },
    baseStyle: {
      position: `relative`,
      fontFamily: `heading`,
      textTransform: `uppercase`,
      cursor: `pointer`,
      letterSpacing: `0.11rem`
    },
    sizes: {
      ellipsedSmall: {
        fontSize: `md`
      },
      ellipsedMedium: {
        fontSize: `2xl`
      },
      sm: ({ variant }) => ({
        h: variant === `primary` || `secondary` ? `3.438rem` : null,
        px: variant === `primary` || `secondary` ? `2.375rem` : null,
        minW: null,
        fontSize: `md`,
        '.button__inner': {
          h: variant === `primary` || `secondary` ? `1.188rem` : null
        }
      }),
      md: ({ variant }) => ({
        h: variant === `primary` || `secondary` ? `4.5rem` : null,
        px: variant === `primary` || `secondary` ? `4rem` : null,
        minW: null,
        fontSize: `xl`,
        '.button__inner': {
          h: variant === `primary` || `secondary` ? `1.625rem` : null
        }
      })
    },
    variants: {
      primary: {
        backgroundColor: `transparent`,
        color: `colours.darkPurple`,
        borderRadius: `5.5rem`,
        '&::before': {
          content: `""`,
          position: `absolute`,
          top: `0`,
          bottom: `0`,
          left: `0`,
          right: `0`,
          borderRadius: `5.5rem`,
          zIndex: `-1`,
          transition: `transform .5s`,
          backgroundImage: `linear-gradient(90deg, #cea558 0%, #fbe09b 40%, #cea558 100%)`,
          backgroundPosition: `center`,
          backgroundSize: `cover`,
          backgroundRepeat: `no-repeat`
        },
        '&::after': {
          content: `""`,
          position: `absolute`,
          top: `0`,
          bottom: `0`,
          left: `0`,
          right: `0`,
          borderRadius: `5.5rem`,
          zIndex: `-1`,
          transition: `transform .5s, background-color .25s, background-position-y .35s ease-out, background-image 0.35s`,
          backgroundImage: `linear-gradient(90deg, transparent 0%, transparent 100%), /**/url("${buttonBgLight}")`,
          backgroundPosition: `center`,
          backgroundSize: `cover`,
          backgroundRepeat: `no-repeat`,
          backgroundPositionY: `4.5rem`
        },
        '.button__inner': {
          display: `block`,
          position: `relative`,
          '&::after': {
            content: `attr(data-text)`,
            display: `block`
          }
        },
        _hover: {
          '&::before, &::after': {
            transform: { md: `scaleX(1.1)` },
            boxShadow: { md: `darkPurple` }
          },
          '&::after': {
            backgroundColor: `white`,
            backgroundPositionY: `-2rem`,
            transition: `transform .5s, background-color .35s .15s, background-position-y .35s ease-out, background-image 0.35s`
          }
        }
      },
      secondary: {
        backgroundColor: `transparent`,
        color: `white`,
        borderRadius: `5.5rem`,
        '&::after': {
          content: `""`,
          position: `absolute`,
          top: `0`,
          bottom: `0`,
          left: `0`,
          right: `0`,
          backgroundColor: `transparent`,
          borderStyle: `solid`,
          border: `2px solid`,
          borderColor: `colours.gold`,
          borderRadius: `5.5rem`,
          zIndex: `-1`,
          transition: `transform .5s, background-color .25s, border-color .05s .2s, background-position-y .35s ease-out`,
          backgroundImage: `/**/url("${buttonBgDark}")`,
          backgroundPosition: `center`,
          backgroundSize: `cover`,
          backgroundRepeat: `no-repeat`,
          backgroundPositionY: `4.5rem`
        },
        '.button__inner': {
          display: `block`,
          position: `relative`,
          '&::after': {
            content: `attr(data-text)`,
            display: `block`
          }
        },
        _hover: {
          '&::after': {
            backgroundColor: { md: `colours.darkPurple` },
            borderColor: { md: `colours.darkPurple` },
            transform: { md: `scaleX(1.1)` },
            backgroundPositionY: `-2rem`,
            transition: `transform .5s, background-color .35s .05s, border-color .5s .2s, background-position-y .35s ease-out`
          }
        }
      },
      tertiary: {
        color: `white`
      },
      quaternary: {
        color: `white`,
        border: `0.125rem solid`,
        borderColor: `colours.gold`
      }
    }
  },
  Link: {
    baseStyle: {
      _hover: {
        textDecoration: `none`
      }
    },
    variants: {
      header: {
        color: `secondary.pink`,
        fontWeight: `bold`,
        textDecoration: `underline`,
        _hover: {
          textDecoration: `underline`
        }
      }
    }
  },
  Radio: {
    baseStyle: {
      control: {
        borderWidth: `1px`,
        borderColor: `primary.dark-blue`,
        _checked: {
          background: `primary.dark-blue`,
          borderColor: `primary.dark-blue`,
          _hover: {
            background: `primary.dark-blue`,
            borderColor: `primary.dark-blue`
          }
        }
      },
      label: {
        fontFamily: `heading`,
        fontWeight: `bold`,
        color: `secondary.pink`,
        textDecoration: `underline`
      }
    },
    sizes: {
      md: {
        control: {
          w: 6,
          h: 6
        }
      }
    }
  },
  Checkbox: {
    baseStyle: {
      control: {
        borderWidth: `1px`,
        borderColor: `primary.dark-blue`,
        borderRadius: 4,
        _checked: {
          background: `primary.dark-blue`,
          borderColor: `primary.dark-blue`,
          _hover: {
            background: `primary.dark-blue`,
            borderColor: `primary.dark-blue`
          }
        }
      },
      label: {
        fontFamily: `heading`,
        fontWeight: `bold`,
        color: `secondary.pink`,
        textDecoration: `underline`
      }
    },
    sizes: {
      md: {
        control: {
          w: 6,
          h: 6
        }
      }
    }
  }
};
